import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { toast } from "sonner";
import {
  Sun, Check, Upload, FileText, Home, Users, Ruler, Zap, Car, Flame,
  Waves, Wind, CheckCircle2, Sparkles, Send, Loader2, Plus, Minus, MapPin, Compass,
} from "lucide-react";
import { apiClient } from "../lib/api";
import RoofMap from "../components/RoofMap";
import { trackLead } from "../lib/fbpixel";
import { getUtm } from "../lib/utm";
import { getIntakeT, fmt } from "../i18n/intake_strings";
import LanguageSwitcher from "../components/LanguageSwitcher";

/* Intake — Single-page Summer-Sale intake. 6 clean questions + final contact
   wrap-up. Submit to backend (lead → bill → phase → dwelling → fiscal →
   claim free slot → redirect to /portal for roof drawing). */

const SECTION_IDS = [
  { id: "bill",     n: "01" }, { id: "phase",    n: "02" },
  { id: "dwelling", n: "03" }, { id: "usage",    n: "04" },
  { id: "roof",     n: "05" }, { id: "fiscal",   n: "06" },
];

const HOUSING_KEYS = ["vrijstaand", "twee-onder-een-kap", "rijwoning", "appartement", "villa"];
const HOUSING_LABEL_MAP = {
  "vrijstaand": "dwt_detached",
  "twee-onder-een-kap": "dwt_semi",
  "rijwoning": "dwt_terrace",
  "appartement": "dwt_apt",
  "villa": "dwt_villa",
};

const ORIENTATION_KEYS = ["Norte","Noreste","Este","Sureste","Sur","Suroeste","Oeste","Noroeste"];

// Decade blocks → representative midpoint year stored in backend.
const YEAR_BLOCKS = [
  { rep: 1970, labelKey: "yb1" },
  { rep: 1988, labelKey: "yb2" },
  { rep: 2001, labelKey: "yb3" },
  { rep: 2013, labelKey: "yb4" },
  { rep: 2021, labelKey: "yb5" },
];

const INCOME_BRACKETS = [
  { labelKey: "ib1", irpf: 19 },
  { labelKey: "ib2", irpf: 24 },
  { labelKey: "ib3", irpf: 30 },
  { labelKey: "ib4", irpf: 37 },
  { labelKey: "ib5", irpf: 45 },
  { labelKey: "ib6", irpf: 24, unknown: true },
];

const USAGE_OPTIONS = [
  { key: "ev",        icon: Car,  labelKey: "u_ev" },
  { key: "heatpump",  icon: Flame, labelKey: "u_hp" },
  { key: "pool",      icon: Waves, labelKey: "u_pool" },
  { key: "ac",        icon: Wind,  labelKey: "u_ac" },
  { key: "induction", icon: Zap,   labelKey: "u_ind" },
];

export default function Intake() {
  const nav = useNavigate();
  const { i18n } = useTranslation();
  const T = getIntakeT(i18n.language);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  const progressWidth = useTransform(progress, (v) => `${Math.min(100, v * 100)}%`);

  const [active, setActive] = useState("bill");
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [billFile, setBillFile] = useState(null);
  const [phase, setPhase] = useState("unknown");
  const [dwelling, setDwelling] = useState({ housing_type: "vrijstaand", year_rep: 2001, m2: 100, persons: 2, bathrooms: 2, is_primary: true });
  const [usage, setUsage] = useState({
    ev: false, heatpump: false, pool: false, ac: false, induction: false,
    wp_water: false, wp_heating: false,
    pool_m3: 40, pool_covered: false,
    pool_wants_heating: false, pool_heating_type: "",
  });
  const [fiscal, setFiscal] = useState({ resident: true, irpf: 24, bracket_key: "ib6" });
  const [roof, setRoof] = useState({
    polygon: [], pitch: 25, orientation: "Sur",
    pitch_auto: false, orientation_auto: false,
  });
  const [contact, setContact] = useState({ name: "", email: "", whatsapp: "" });

  // Refs to avoid stale closures
  const usageRef = useRef(usage);
  const dwellingRef = useRef(dwelling);
  const fiscalRef = useRef(fiscal);
  const roofRef = useRef(roof);
  useEffect(() => { usageRef.current = usage; }, [usage]);
  useEffect(() => { dwellingRef.current = dwelling; }, [dwelling]);
  useEffect(() => { fiscalRef.current = fiscal; }, [fiscal]);
  useEffect(() => { roofRef.current = roof; }, [roof]);

  // Section tracking
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.target.offsetTop - b.target.offsetTop);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: 0 }
    );
    SECTION_IDS.concat([{ id: "contact" }]).forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
  };

  const handleBillFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(T.file_too_big); return; }
    setBillFile(file);
  };
  const onDrop = (e) => { e.preventDefault(); handleBillFile(e.dataTransfer.files?.[0]); };
  const onPick = (e) => handleBillFile(e.target.files?.[0]);
  const toggleUsage = (k) => setUsage((u) => ({ ...u, [k]: !u[k] }));

  const submit = async () => {
    if (!contact.name || !contact.email) {
      toast.error(T.fill_required);
      scrollTo("contact"); return;
    }
    setSubmitting(true);
    const u = usageRef.current;
    const d = dwellingRef.current;
    const f = fiscalRef.current;
    const rf = roofRef.current;
    try {
      const utm = getUtm();
      const region = "madrid";
      const lang = (i18n.language || "es").split("-")[0].toLowerCase();
      const r = await apiClient.post("/leads", {
        name: contact.name,
        email: contact.email,
        whatsapp: contact.whatsapp || "—",
        region,
        language: lang,
        funnel: "summer-sale",
        ...(utm ? { utm } : {}),
      });
      const token = r.data.magic_token;

      if (billFile) {
        const fd = new FormData();
        fd.append("token", token);
        fd.append("file", billFile);
        try { await apiClient.post("/intake/bill", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { console.warn("bill upload failed", e); }
      }
      try { await apiClient.post("/intake/phase", { token, phase_type: phase }); }
      catch (e) { console.warn("phase failed", e); }
      // Roof polygon (if user drew one)
      if (rf?.polygon && rf.polygon.length >= 3) {
        try {
          await apiClient.post(`/intake/roof?token=${encodeURIComponent(token)}`, {
            polygon: rf.polygon,
            pitch_deg: rf.pitch,
            orientation: rf.orientation,
          });
        } catch (e) { console.warn("roof failed", e); }
      }
      try {
        await apiClient.post(`/intake/dwelling?token=${encodeURIComponent(token)}`, {
          m2: Number(d.m2) || 100,
          bathrooms: Number(d.bathrooms) || 2,
          persons: Number(d.persons) || 2,
          is_primary: !!d.is_primary,
          construction_year: Number(d.year_rep) || null,
        });
      } catch (e) { console.warn("dwelling failed", e); }
      try {
        await apiClient.post(`/intake/fiscal?token=${encodeURIComponent(token)}`, {
          is_resident: !!f.resident,
          has_ev: !!u.ev,
          has_heatpump: !!(u.wp_water || u.wp_heating || u.heatpump),
          has_pool: !!u.pool,
          wp_water: !!u.wp_water,
          wp_heating: !!u.wp_heating,
          pool: u.pool ? {
            m3: Number(u.pool_m3) || 0,
            covered: !!u.pool_covered,
            wants_heating: !!u.pool_wants_heating,
            heating_type: u.pool_heating_type || null,
          } : null,
        });
      } catch (e) { console.warn("fiscal failed", e); }
      let claim = null;
      try {
        const c = await apiClient.post("/free-intake/claim", { token });
        claim = c.data;
      } catch (e) { console.warn("claim failed", e); }
      trackLead({ content_name: "DoctorSol Summer Sale", content_category: "summer-sale" });
      toast.success(claim?.claimed ? T.toast_ok : "OK");
      setTimeout(() => nav(`/portal?token=${token}`), 800);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || T.err_generic);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', 'Helvetica Neue', Arial, sans-serif", WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" }}>
      <style>{`
        .serif { font-family: inherit; letter-spacing: -0.035em; font-weight: 600; }
        h1, h2, h3 { letter-spacing: -0.035em; font-weight: 600; }
      `}</style>

      <header className="sticky top-0 z-30 bg-[#F8F6F0]/80 backdrop-blur-md border-b border-[#0F172A]/5">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 rounded-full bg-[#E78938] flex items-center justify-center">
              <Sun className="w-4 h-4 text-white" />
            </div>
            <span className="serif text-lg font-medium">DoctorSol</span>
          </Link>
          <div className="hidden md:flex items-center gap-3 text-xs text-[#475569]">
            {active === "contact" ? (
              <span className="text-[#E78938] font-semibold">{T.almost}</span>
            ) : (
              <>
                <span>{T.q}</span>
                <span className="serif text-base text-[#0F172A] font-medium">
                  {SECTION_IDS.find((s) => s.id === active)?.n || "01"}
                </span>
                <span className="text-[#94A3B8]">{T.of_} 06</span>
              </>
            )}
            <LanguageSwitcher variant="minimal" />
          </div>
          <div className="md:hidden">
            <LanguageSwitcher variant="minimal" />
          </div>
        </div>
        <motion.div style={{ width: progressWidth }} className="h-1 bg-[#E78938] origin-left" />
      </header>

      <nav className="hidden lg:block fixed top-1/2 -translate-y-1/2 left-6 z-20">
        <ul className="space-y-3">
          {SECTION_IDS.map((s) => (
            <li key={s.id}>
              <button onClick={() => scrollTo(s.id)} data-testid={`nav-${s.id}`}
                className="group flex items-center gap-3" title={T[`s${parseInt(s.n)}_t`]}>
                <span className={`relative w-2.5 h-2.5 rounded-full transition-all duration-300
                  ${active === s.id ? "bg-[#E78938] scale-125" : "bg-[#0F172A]/15 group-hover:bg-[#0F172A]/40"}`}>
                  {active === s.id && <span className="absolute inset-0 rounded-full bg-[#E78938] animate-ping opacity-40" />}
                </span>
                <span className={`text-xs font-mono transition-all ${active === s.id ? "text-[#0F172A] opacity-100" : "opacity-0 group-hover:opacity-60"}`}>
                  {s.n}
                </span>
              </button>
            </li>
          ))}
          <li className="pt-2 border-t border-[#0F172A]/10">
            <button onClick={() => scrollTo("contact")} data-testid="nav-contact"
              className="group flex items-center gap-3" title={T.nav_close_title}>
              <span className={`w-2.5 h-2.5 rounded-full transition-all ${active === "contact" ? "bg-[#22C55E] scale-125" : "bg-[#0F172A]/15 group-hover:bg-[#0F172A]/40"}`} />
              <span className={`text-xs font-mono ${active === "contact" ? "text-[#22C55E] opacity-100" : "opacity-0 group-hover:opacity-60"}`}>
                ✓
              </span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Intro */}
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-16">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-[#E78938] text-white px-4 py-2 rounded-full text-sm font-semibold mb-8">
            <Sparkles className="w-4 h-4" />
            {T.badge}
          </div>
          <h1 className="text-5xl sm:text-6xl tracking-tighter leading-[0.95] mb-5" style={{ whiteSpace: "pre-line" }}>
            {T.hero_t}
          </h1>
          <p className="text-lg text-[#475569] leading-relaxed">
            {T.hero_p}
          </p>
        </div>
      </section>

      <Section id="bill" n="01" title={T.s1_t} sub={T.s1_s} total={6}
        why={T.s1_w} T={T}>
        <BillUpload file={billFile} onDrop={onDrop} onPick={onPick} T={T} />
      </Section>

      <Section id="phase" n="02" title={T.s2_t} sub={T.s2_s} alt total={6}
        why={T.s2_w} T={T}>
        <PhaseCard value={phase} onChange={setPhase} T={T} />
      </Section>

      <Section id="dwelling" n="03" title={T.s3_t} sub={T.s3_s} total={6}
        why={T.s3_w} T={T}>
        <DwellingCard value={dwelling} onChange={setDwelling} T={T} />
      </Section>

      <Section id="usage" n="04" title={T.s4_t} sub={T.s4_s} alt total={6}
        why={T.s4_w} T={T}>
        <UsageCard value={usage} onToggle={toggleUsage} onChange={setUsage} T={T} />
      </Section>

      <Section id="roof" n="05" title={T.s5_t} sub={T.s5_s} total={6}
        why={T.s5_w} T={T}>
        <RoofSection value={roof} onChange={setRoof} T={T} />
      </Section>

      <Section id="fiscal" n="06" title={T.s6_t} sub={T.s6_s} alt total={6}
        why={T.s6_w} T={T}>
        <FiscalCard value={fiscal} onChange={setFiscal} T={T} />
      </Section>

      {/* Final wrap-up — visually distinct from questions */}
      <section id="contact" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#0F172A] text-white">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-[#22C55E]/20 text-[#22C55E] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <CheckCircle2 className="w-4 h-4" />
            {T.final_badge}
          </div>
          <h2 className="text-4xl sm:text-5xl tracking-tighter leading-tight mb-4" style={{ whiteSpace: "pre-line" }}>{T.final_t}</h2>
          <p className="text-lg text-white/70 leading-relaxed mb-10">{T.final_p}</p>
          <ContactCard value={contact} onChange={setContact} T={T} />
          <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <button onClick={submit} disabled={submitting} data-testid="submit-report"
              className="inline-flex items-center gap-3 bg-[#E78938] text-white rounded-full px-10 py-5 text-lg font-medium hover:bg-[#D17627] hover:shadow-[0_8px_32px_rgba(231,137,56,0.3)] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {submitting ? T.submit_busy : T.submit}
            </button>
            <p className="text-xs text-white/60">{T.summer_footer}</p>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-[#0F172A]/10 px-6 md:px-12 lg:px-24 py-8 text-xs text-[#94A3B8] text-center">
        {T.footer_text}
      </footer>
    </div>
  );
}

/* ───── Section wrapper ──────────────────────────────────────────────────── */
function Section({ id, n, title, sub, why, alt = false, total = 5, children, T }) {
  return (
    <section id={id} className={`px-6 md:px-12 lg:px-24 py-20 md:py-28 ${alt ? "bg-white border-y border-[#0F172A]/5" : ""}`}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15% 0px" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl mx-auto"
      >
        <div className="flex items-baseline gap-4 mb-3">
          <span className="text-5xl text-[#E78938]/30 font-semibold serif">{n}</span>
          <span className="text-xs uppercase tracking-widest text-[#E78938] font-semibold">
            {fmt(T.vraag_total, { n: parseInt(n), total })}
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl tracking-tight leading-tight mb-2">{title}</h2>
        <p className="text-[#475569] mb-2 text-lg">{sub}</p>
        <p className="text-sm text-[#94A3B8] italic mb-10 leading-relaxed">↳ {why}</p>
        {children}
      </motion.div>
    </section>
  );
}

/* ───── Question bodies ──────────────────────────────────────────────────── */
const BillUpload = ({ file, onDrop, onPick, T }) => (
  <label data-testid="bill-dropzone"
    onDragOver={(e) => e.preventDefault()}
    onDrop={onDrop}
    className="block border-2 border-dashed border-[#E78938]/30 bg-[#E78938]/5 rounded-2xl p-12 text-center hover:bg-[#E78938]/10 hover:border-[#E78938]/50 transition-all cursor-pointer group">
    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onPick} className="hidden" data-testid="bill-input" />
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-md group-hover:scale-110 transition-transform mb-5">
      <Upload className="w-7 h-7 text-[#E78938]" />
    </div>
    {file ? (
      <>
        <div className="serif text-xl mb-1.5 text-[#22C55E]">✓ {file.name}</div>
        <div className="text-sm text-[#475569]">{Math.round(file.size / 1024)} KB</div>
      </>
    ) : (
      <>
        <div className="serif text-xl mb-1.5">{T.drag}</div>
        <div className="text-sm text-[#475569] mb-5">PDF · JPG · PNG · max 10 MB</div>
        <div className="inline-flex items-center gap-2 bg-white border border-[#0F172A]/10 rounded-full px-5 py-2.5 text-sm hover:border-[#E78938] transition-colors">
          <FileText className="w-4 h-4 text-[#E78938]" /> {T.or_click}
        </div>
      </>
    )}
  </label>
);

const PhaseCard = ({ value, onChange, T }) => (
  <div className="space-y-3">
    {[
      { v: "monofasico", label: T.phase_mono_l, sub: T.phase_mono_s },
      { v: "trifasico",  label: T.phase_tri_l,  sub: T.phase_tri_s },
      { v: "unknown",    label: T.phase_unk_l,  sub: T.phase_unk_s },
    ].map((o) => (
      <button type="button" key={o.v} data-testid={`phase-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`w-full flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all text-left ${value === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>
        <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 ${value === o.v ? "border-[#E78938] bg-[#E78938]" : "border-[#0F172A]/30"}`}>
          {value === o.v && <div className="w-2 h-2 bg-white rounded-full" />}
        </div>
        <div>
          <div className="serif text-base">{o.label}</div>
          <div className="text-xs text-[#475569] mt-0.5">{o.sub}</div>
        </div>
      </button>
    ))}
  </div>
);

const Stepper = ({ value, onChange, step = 1, min = 0, max = 999, suffix }) => (
  <div className="flex items-center bg-white border border-[#0F172A]/10 rounded-xl overflow-hidden">
    <button type="button"
      onClick={() => onChange(Math.max(min, Number(value || 0) - step))}
      className="w-12 h-12 flex items-center justify-center hover:bg-[#F8F6F0] active:bg-[#E78938]/10 transition-colors">
      <Minus className="w-4 h-4 text-[#0F172A]" />
    </button>
    <div className="flex-1 text-center serif text-xl font-medium tabular-nums">
      {value}{suffix && <span className="text-[#94A3B8] text-base ml-1">{suffix}</span>}
    </div>
    <button type="button"
      onClick={() => onChange(Math.min(max, Number(value || 0) + step))}
      className="w-12 h-12 flex items-center justify-center hover:bg-[#F8F6F0] active:bg-[#E78938]/10 transition-colors">
      <Plus className="w-4 h-4 text-[#0F172A]" />
    </button>
  </div>
);

const DwellingCard = ({ value, onChange, T }) => (
  <div className="space-y-6">
    {/* Bouwjaar als blokken */}
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-3 flex items-center gap-2">
        {T.dw_year_label}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {YEAR_BLOCKS.map((b) => (
          <button key={b.labelKey} type="button" data-testid={`dwelling-year-${b.rep}`}
            onClick={() => onChange({ ...value, year_rep: b.rep })}
            className={`p-3 rounded-xl border-2 text-sm transition-all ${value.year_rep === b.rep ? "border-[#E78938] bg-[#E78938]/5 text-[#0F172A]" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white text-[#475569]"}`}>
            {T[b.labelKey]}
          </button>
        ))}
      </div>
    </div>

    {/* Oppervlakte + Bewoners met steppers */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Ruler className="w-4 h-4 text-[#E78938]" />
          <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.dw_m2_label}</span>
        </div>
        <Stepper value={value.m2} onChange={(v) => onChange({ ...value, m2: v })}
          step={5} min={20} max={1000} suffix="m²" />
      </div>
      <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-[#E78938]" />
          <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.dw_persons_label}</span>
        </div>
        <Stepper value={value.persons} onChange={(v) => onChange({ ...value, persons: v })}
          step={1} min={1} max={12} />
      </div>
      <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Home className="w-4 h-4 text-[#E78938]" />
          <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.dw_bath_label}</span>
        </div>
        <Stepper value={value.bathrooms} onChange={(v) => onChange({ ...value, bathrooms: v })}
          step={1} min={1} max={6} />
      </div>
      <div className="bg-white border border-[#0F172A]/5 rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-3">{T.dw_primary_label}</div>
        <div className="grid grid-cols-2 gap-2">
          {[{ v: true, l: T.yes }, { v: false, l: T.no }].map((o) => (
            <button key={String(o.v)} type="button" data-testid={`dwelling-primary-${o.v}`}
              onClick={() => onChange({ ...value, is_primary: o.v })}
              className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-all ${value.is_primary === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>{o.l}</button>
          ))}
        </div>
      </div>
    </div>

    {/* Woningtype */}
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-3">{T.dw_type_label}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {HOUSING_KEYS.map((t) => (
          <button key={t} type="button" data-testid={`dwelling-type-${t}`}
            onClick={() => onChange({ ...value, housing_type: t })}
            className={`p-3 rounded-xl border-2 text-sm transition-all ${value.housing_type === t ? "border-[#E78938] bg-[#E78938]/5 text-[#0F172A]" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white text-[#475569]"}`}>
            {T[HOUSING_LABEL_MAP[t]]}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const UsageCard = ({ value, onToggle, onChange, T }) => {
  const setKey = (k, v) => onChange((u) => ({ ...u, [k]: typeof v === "function" ? v(u[k]) : v }));
  // Convert usage_q markdown-style **...** into bold
  const renderBoldMarkdown = (str) => {
    const parts = String(str || "").split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>
    );
  };
  return (
    <div className="space-y-5">
      <p className="text-sm text-[#475569]">
        {renderBoldMarkdown(T.usage_q)}
      </p>
      <p className="text-xs text-[#94A3B8] italic">↳ {T.usage_hint}</p>
      <div className="flex flex-wrap gap-2.5">
        {USAGE_OPTIONS.map((o) => {
          const on = !!value[o.key];
          return (
            <button key={o.key} type="button" data-testid={`usage-${o.key}`} onClick={() => onToggle(o.key)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 text-sm transition-all ${on ? "border-[#E78938] bg-[#E78938] text-white shadow-md" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>
              <o.icon className="w-4 h-4" /> {T[o.labelKey]}
            </button>
          );
        })}
      </div>

      {value.heatpump && (
        <div className="bg-[#F8F6F0] border border-[#0F172A]/5 rounded-2xl p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-[#E78938] font-semibold">{T.wp_q}</div>
          {[
            { k: "wp_water",   label: T.wp_water_l,   desc: T.wp_water_d },
            { k: "wp_heating", label: T.wp_heating_l, desc: T.wp_heating_d },
          ].map((o) => {
            const checked = !!value[o.k];
            return (
              <button type="button" key={o.k} data-testid={`usage-${o.k}`}
                onClick={() => setKey(o.k, (prev) => !prev)}
                className="w-full flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-white transition-colors text-left">
                <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${checked ? "border-[#E78938] bg-[#E78938]" : "border-[#0F172A]/30"}`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <div className="text-base font-medium">{o.label}</div>
                  <div className="text-xs text-[#475569]">{o.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {value.pool && (
        <div className="bg-[#F8F6F0] border border-[#0F172A]/5 rounded-2xl p-5 space-y-5">
          <div className="text-[10px] uppercase tracking-wider text-[#E78938] font-semibold">{T.pool_q}</div>

          {/* Volume stepper */}
          <div className="bg-white border border-[#0F172A]/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Waves className="w-4 h-4 text-[#E78938]" />
              <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.pool_vol_l}</span>
              <span className="ml-auto text-xs text-[#94A3B8]">{fmt(T.pool_vol_aux, { m2: Math.round((Number(value.pool_m3) || 0) * 0.5) })}</span>
            </div>
            <Stepper value={value.pool_m3} onChange={(v) => setKey("pool_m3", v)}
              step={5} min={5} max={200} suffix="m³" />
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-[#475569]">
              <button type="button" onClick={() => setKey("pool_m3", 20)} className="py-1.5 rounded-lg border border-[#0F172A]/10 hover:border-[#E78938] transition-colors">{T.pool_small}</button>
              <button type="button" onClick={() => setKey("pool_m3", 40)} className="py-1.5 rounded-lg border border-[#0F172A]/10 hover:border-[#E78938] transition-colors">{T.pool_std}</button>
              <button type="button" onClick={() => setKey("pool_m3", 80)} className="py-1.5 rounded-lg border border-[#0F172A]/10 hover:border-[#E78938] transition-colors">{T.pool_large}</button>
            </div>
          </div>

          {/* Afgedekt */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-2">{T.pool_cover_q}</div>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: true, l: T.pool_cover_yes }, { v: false, l: T.pool_cover_no }].map((o) => (
                <button key={String(o.v)} type="button" data-testid={`usage-pool-covered-${o.v}`}
                  onClick={() => setKey("pool_covered", o.v)}
                  className={`p-3 rounded-xl border-2 text-sm transition-all ${value.pool_covered === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>{o.l}</button>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1.5">↳ {T.pool_cover_hint}</p>
          </div>

          {/* Wil je zwembad verwarmen? */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-2">{T.pool_heat_q}</div>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: true, l: T.pool_heat_yes }, { v: false, l: T.pool_heat_no }].map((o) => (
                <button key={String(o.v)} type="button" data-testid={`usage-pool-heating-${o.v}`}
                  onClick={() => setKey("pool_wants_heating", o.v)}
                  className={`p-3 rounded-xl border-2 text-sm transition-all ${value.pool_wants_heating === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>{o.l}</button>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1.5">↳ {T.pool_heat_hint}</p>
          </div>

          {/* Type verwarming, alleen als ze willen verwarmen */}
          {value.pool_wants_heating && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-2">{T.pool_heat_type_q}</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { v: "aerotermia", l: T.pht_aero_l,  d: T.pht_aero_d },
                  { v: "solar",      l: T.pht_solar_l, d: T.pht_solar_d },
                  { v: "unknown",    l: T.pht_unk_l,   d: T.pht_unk_d },
                ].map((o) => (
                  <button key={o.v} type="button" data-testid={`usage-pool-heating-type-${o.v}`}
                    onClick={() => setKey("pool_heating_type", o.v)}
                    className={`p-3 rounded-xl border-2 text-sm transition-all text-left ${value.pool_heating_type === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>
                    <div className="font-medium">{o.l}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5">{o.d}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Derive a compass orientation (Norte / Noreste / ... / Noroeste) from a
// polygon by looking at the perpendicular of its longest edge. Used when the
// user clicks "Weet ik niet" — we infer the roof slope direction from the
// polygon they just drew.
function deriveOrientationFromPolygon(poly) {
  if (!poly || poly.length < 3) return "Sur";
  let longest = { len: 0, angle: 0 };
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const dLat = b[0] - a[0];
    const dLng = b[1] - a[1];
    const len = Math.sqrt(dLat * dLat + dLng * dLng);
    if (len > longest.len) longest = { len, angle: Math.atan2(dLng, dLat) };
  }
  let deg = ((longest.angle * 180) / Math.PI + 90 + 360) % 360;
  return ORIENTATION_KEYS[Math.round(deg / 45) % 8];
}

const RoofSection = ({ value, onChange, T }) => {
  const setPolygon = (poly) => onChange((v) => {
    const next = { ...v, polygon: poly };
    if (v.orientation_auto && poly.length >= 3) {
      next.orientation = deriveOrientationFromPolygon(poly);
    }
    return next;
  });
  const ok = value.polygon.length >= 3;
  const toggleAutoPitch = () => onChange((v) => ({
    ...v,
    pitch_auto: !v.pitch_auto,
    pitch: !v.pitch_auto ? 25 : v.pitch,
  }));
  const toggleAutoOrient = () => onChange((v) => ({
    ...v,
    orientation_auto: !v.orientation_auto,
    orientation: !v.orientation_auto
      ? deriveOrientationFromPolygon(v.polygon)
      : v.orientation,
  }));
  // Translated orientation labels look-up (state still stores Spanish keys)
  const orientLabel = (key) => (T.orientations && T.orientations[key]) || key;
  return (
    <div className="space-y-5">
      <RoofMap
        initial={value.polygon}
        onChange={setPolygon}
      />
      {!ok && (
        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 text-sm text-[#475569] flex items-start gap-3">
          <MapPin className="w-5 h-5 text-[#E78938] shrink-0 mt-0.5" />
          <div>
            <strong className="text-[#0F172A] block">{T.roof_pre_title}</strong>
            {T.roof_pre_body}
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Hellingshoek */}
        <div className={`bg-white border-2 rounded-2xl p-5 transition-all ${value.pitch_auto ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-[#0F172A]/5"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Compass className="w-4 h-4 text-[#E78938]" />
            <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.roof_pitch_l}</span>
            <span className="ml-auto serif text-base text-[#E78938] font-semibold">
              {value.pitch}°{value.pitch_auto && ` (${T.roof_pitch_auto})`}
            </span>
          </div>
          <input type="range" min={5} max={45} step={5}
            value={value.pitch} disabled={value.pitch_auto}
            onChange={(e) => onChange((v) => ({ ...v, pitch: Number(e.target.value), pitch_auto: false }))}
            data-testid="roof-pitch"
            className="w-full accent-[#E78938] disabled:opacity-40" />
          <div className="flex justify-between text-xs text-[#94A3B8] mt-1">
            <span>{T.roof_pitch_flat}</span><span>{T.roof_pitch_mid}</span><span>{T.roof_pitch_steep}</span>
          </div>
          <button type="button" onClick={toggleAutoPitch} data-testid="roof-pitch-auto"
            className={`mt-3 w-full text-xs py-2 rounded-lg border transition-colors ${value.pitch_auto ? "border-[#22C55E] bg-[#22C55E] text-white" : "border-[#0F172A]/15 text-[#475569] hover:border-[#E78938] hover:text-[#E78938]"}`}>
            {value.pitch_auto ? T.roof_pitch_auto_on : T.roof_pitch_auto_off}
          </button>
        </div>

        {/* Oriëntatie */}
        <div className={`bg-white border-2 rounded-2xl p-5 transition-all ${value.orientation_auto ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-[#0F172A]/5"}`}>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-[#E78938]" />
            <span className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">{T.roof_orient_l}</span>
            {value.orientation_auto && (
              <span className="ml-auto text-[10px] text-[#22C55E] font-semibold">{T.roof_pitch_auto}</span>
            )}
          </div>
          <select value={value.orientation} disabled={value.orientation_auto}
            onChange={(e) => onChange((v) => ({ ...v, orientation: e.target.value, orientation_auto: false }))}
            data-testid="roof-orientation"
            className="w-full bg-[#F8F6F0] rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#E78938]/40 text-base font-medium disabled:opacity-60">
            {ORIENTATION_KEYS.map((o) => <option key={o} value={o}>{orientLabel(o)}</option>)}
          </select>
          <div className="text-xs text-[#94A3B8] mt-2">{T.roof_orient_hint}</div>
          <button type="button" onClick={toggleAutoOrient} data-testid="roof-orientation-auto"
            className={`mt-3 w-full text-xs py-2 rounded-lg border transition-colors ${value.orientation_auto ? "border-[#22C55E] bg-[#22C55E] text-white" : "border-[#0F172A]/15 text-[#475569] hover:border-[#E78938] hover:text-[#E78938]"}`}>
            {value.orientation_auto
              ? (ok ? fmt(T.roof_orient_auto_with_val, { val: orientLabel(value.orientation) }) : T.roof_orient_auto_pre)
              : T.roof_orient_auto_off}
          </button>
        </div>
      </div>
      {ok && (
        <div className="bg-[#22C55E]/5 border border-[#22C55E]/20 rounded-xl p-4 text-sm text-[#0F172A] flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#22C55E] shrink-0" />
          {fmt(T.roof_ok, { n: value.polygon.length })}
        </div>
      )}
    </div>
  );
};

const FiscalCard = ({ value, onChange, T }) => (
  <div className="space-y-6">
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-3">{T.fis_res_q}</div>
      <div className="grid grid-cols-2 gap-2.5">
        {[{ v: true, l: T.fis_res_yes }, { v: false, l: T.fis_res_no }].map((o) => (
          <button key={String(o.v)} type="button" data-testid={`fiscal-resident-${o.v}`}
            onClick={() => onChange({ ...value, resident: o.v })}
            className={`p-4 rounded-xl border-2 text-sm transition-all text-left ${value.resident === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>
            <div className="serif text-base">{o.l}</div>
          </button>
        ))}
      </div>
    </div>

    {value.resident ? (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold mb-3">{T.fis_inc_q}</div>
        <p className="text-xs text-[#94A3B8] mb-3 italic">↳ {T.fis_inc_hint}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INCOME_BRACKETS.map((b) => (
            <button key={b.labelKey} type="button" data-testid={`fiscal-bracket-${b.irpf}`}
              onClick={() => onChange({ ...value, irpf: b.irpf, bracket_key: b.labelKey })}
              className={`p-3 rounded-xl border-2 text-sm transition-all text-left ${value.bracket_key === b.labelKey ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#0F172A]/30 bg-white"}`}>
              <div className="font-medium">{T[b.labelKey]}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">
                {b.unknown ? T.ih_unknown : fmt(T.ih_bracket, { pct: b.irpf })}
              </div>
            </button>
          ))}
        </div>
      </div>
    ) : (
      <div className="bg-[#F8F6F0] border border-[#0F172A]/10 rounded-xl p-5 text-sm text-[#475569] leading-relaxed">
        <strong className="text-[#0F172A] block mb-2">{T.fis_non_res_title}</strong>
        {T.fis_non_res_body}
      </div>
    )}
  </div>
);

const ContactCard = ({ value, onChange, T }) => (
  <div className="space-y-4">
    {[
      { k: "name",     label: T.ct_name_l,  placeholder: T.ct_name_ph,  required: true },
      { k: "email",    label: T.ct_email_l, placeholder: T.ct_email_ph, required: true, type: "email" },
      { k: "whatsapp", label: T.ct_wa_l,    placeholder: T.ct_wa_ph },
    ].map((f) => (
      <div key={f.k}>
        <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold block mb-1.5">
          {f.label}{f.required && <span className="text-[#E78938]"> *</span>}
        </label>
        <input value={value[f.k] || ""} placeholder={f.placeholder} type={f.type || "text"}
          onChange={(e) => onChange({ ...value, [f.k]: e.target.value })}
          data-testid={`contact-${f.k}`}
          className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-3.5 text-base text-white placeholder:text-white/40 focus:outline-none focus:border-[#E78938] focus:ring-2 focus:ring-[#E78938]/30 transition-all" />
      </div>
    ))}
  </div>
);
